import { useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import { useDropzone } from "react-dropzone";
import { Add, Delete } from "@mui/icons-material";
import { registryParticipantDocumentUploadUrl } from "../../../utils/upload";
import http from "../../../utils/http";
import {
  previewWrapper,
  uploadWrapper,
} from "../../QuestionBuilder/QuestionProperties/BasicTab";
import { Avatar, IconButton, Stack } from "@mui/material";
import { useParams } from "react-router-dom";

export const documentTypeOptions = [
  { label: "Protocol Documents", value: "Protocol Documents" },
  { label: "IRB document", value: "IRB document" },
  { label: "Agreements", value: "Agreements" },
];

const UploadItem: React.FC<any> = ({ file, setFieldValue, participantId }) => {
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      try {
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 10 MB!");
            return;
          }
          setLoading(true);
          const url = await registryParticipantDocumentUploadUrl(
            file,
            participantId
          );
          if (url) {
            const obj = {
              previewUrl: url?.previewUrl,
              url: url?.postUploadImageUrl,
              name: file.name,
              type: file.type,
            };
            setFieldValue("file", obj);
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [setFieldValue, participantId]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-powerpoint": [],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
  });

  const removeFile = () => {
    setFieldValue("file", null);
  };

  const isImage = file?.type?.startsWith("image/");

  return (
    <Stack gap={1}>
      {loading ? (
        <Box sx={{ color: "text.secondary" }}>
          <CircularProgress />
        </Box>
      ) : file ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box {...getRootProps({ className: "dropzone" })} sx={previewWrapper}>
            <input {...getInputProps()} />
            {isImage ? (
              <Avatar
                src={file.previewUrl}
                variant="rounded"
                sx={{ width: 200, height: 140 }}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "regular",
                  color: "text.secondary",
                }}
              >
                {file.name}
              </Typography>
            )}
          </Box>
          <IconButton
            color={"error"}
            sx={{ height: 40, width: 40 }}
            onClick={removeFile}
          >
            <Delete />
          </IconButton>
        </Box>
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1}
          {...getRootProps({ className: "dropzone" })}
          sx={uploadWrapper}
        >
          <input {...getInputProps()} />

          <Stack alignItems={"center"} gap={1}>
            <Add fontSize="medium" sx={{ color: "text.secondary" }} />
            <Typography
              sx={{ color: "text.secondary" }}
              variant="body1"
              fontWeight={"medium"}
              textAlign={"center"}
            >
              Add Document <br />
              <Typography
                sx={{ color: "text.secondary" }}
                component={"span"}
                variant="body1"
                fontWeight={"medium"}
              >
                or drag and drop
              </Typography>
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const schema = yup.object().shape({
  documentName: yup.string().required("Document name is Required"),
  documentType: yup.string().required("Document type is Required"),
  documentCategory: yup.string().required("Document category is Required"),
  file: yup.mixed().required("File is Required"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  refreshPage: () => void;
};

const AddDocumentModal = ({ showModal, closeModal, refreshPage }: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const { participantId } = useParams();
  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);

      const body: any = {
        participantId,
        name: values?.documentName,
        type: values?.documentType,
        category: values?.documentCategory,
        url: values?.file?.url,
      };

      const res = await http.post(`/participant-documents/registry`, body);
      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Add Document" onCloseClick={closeModal} />
        <Formik
          initialValues={{
            documentName: "",
            documentType: "",
            documentCategory: "",
            file: null,
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-name">
                  Document Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Enter document name"
                  id="document-name"
                  {...getFieldProps("documentName")}
                  error={
                    touched?.documentName && errors?.documentName ? true : false
                  }
                  helperText={
                    touched?.documentName && errors?.documentName
                      ? (errors?.documentName as string)
                      : " "
                  }
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-type">
                  Document Type <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  value={values?.documentType || ""}
                  onChange={(e) =>
                    setFieldValue("documentType", e.target.value)
                  }
                  fullWidth
                  id="document-type"
                >
                  {documentTypeOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={
                    touched?.documentType && errors?.documentType ? true : false
                  }
                >
                  {touched?.documentType && errors?.documentType
                    ? (errors?.documentType as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="document-category">
                  Document Category <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Enter document category"
                  id="document-category"
                  {...getFieldProps("documentCategory")}
                  error={
                    touched?.documentCategory && errors?.documentCategory
                      ? true
                      : false
                  }
                  helperText={
                    touched?.documentCategory && errors?.documentCategory
                      ? (errors?.documentCategory as string)
                      : " "
                  }
                />
              </FormControl>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle}>
                  Upload File <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <UploadItem
                  file={values?.file}
                  setFieldValue={setFieldValue}
                  participantId={participantId}
                />
                <FormHelperText
                  error={touched?.file && errors?.file ? true : false}
                >
                  {touched?.file && errors?.file
                    ? (errors?.file as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Upload Document
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddDocumentModal;
